import React, { useState } from 'react';
import { UserPlus, Search, X } from 'lucide-react';
import Modal from './modals/Modal';

interface AddFriendModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddFriend: (email: string) => Promise<void>;
    friends: Player[];
}

export default function AddFriendModal({ isOpen, onClose, onAddFriend, friends }: AddFriendModalProps) {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setLoading(true);

        try {
            await onAddFriend(email);
            setSuccess('Friend added successfully!');
            setEmail('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error adding friend');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setEmail('');
        setError(null);
        setSuccess(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Add Friend">
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <UserPlus className="w-8 h-8 text-blue-600" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Add Friend by Email</h3>
                        <p className="text-gray-600">Search for friends by their email address to see their games and availabilities.</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                            {success}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Friend's Email
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="email"
                                required
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter friend's email address"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Adding...' : 'Add Friend'}
                        </button>
                    </div>
                </form>

                {friends.length > 0 && (
                    <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Your Friends ({friends.length})</h4>
                        <div className="space-y-2">
                            {friends.map((friend) => (
                                <div key={friend.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-sm font-medium text-blue-600">
                                                {friend.name?.charAt(0) || '?'}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{friend.name}</p>
                                            <p className="text-xs text-gray-500">{friend.email}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            // TODO: Implement remove friend functionality
                                            console.log('Remove friend:', friend.id);
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                        title="Remove friend"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
